import { IconProps } from '@tamagui/helpers-icon';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from '@tamagui/lucide-icons';
import { Pressable, PressableProps } from '@unistocks/components/basic/pressable';
import { Row } from '@unistocks/components/layout/flex';
import { Select } from '@unistocks/components/select/select';
import { Text } from '@unistocks/components/text/text';
import { t } from '@unistocks/localization';
import { SelectOption } from '@unistocks/types';
import { useMemo } from 'react';
import { ViewProps } from 'tamagui';

export type PaginatorProps = ViewProps & {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
};

export const Paginator: React.FC<PaginatorProps> = ({
  totalItems,
  currentPage,
  itemsPerPage,
  onPageChange,
  ...rest
}) => {
  const lastPage = Math.ceil(totalItems / itemsPerPage);
  const canGoBack = currentPage > 1;
  const canGoForward = currentPage < lastPage;

  const pages = useMemo<SelectOption<number>[]>(
    () =>
      Array.from({ length: lastPage }, (_, i) => i + 1).map(page => ({
        key: page.toString(),
        value: page
      })),
    [lastPage]
  );

  return (
    <Row
      justifyContent="space-between"
      alignItems="center"
      $md={{ gap: '$5', justifyContent: 'center' }}
      {...rest}
    >
      <Pressable
        disabled={!canGoBack}
        onPress={() => onPageChange(1)}
        testID="first-page-button"
        {...triggerProps}
      >
        <ChevronFirst {...iconProps} />
      </Pressable>
      <Pressable
        disabled={!canGoBack}
        onPress={() => onPageChange(currentPage - 1)}
        testID="previous-page-button"
        {...triggerProps}
      >
        <ChevronLeft {...iconProps} />
      </Pressable>

      <Select
        isRequired
        options={pages}
        value={currentPage}
        onChange={onPageChange}
        triggerProps={{ ...triggerProps, testID: 'page-select' }}
        trigger={
          <Pressable minW={'$1'} alignItems="center">
            <Text size={'$6'} fw={'$7'} color={'$bodyFont'}>
              {currentPage}
            </Text>
          </Pressable>
        }
        headerTitle={t('::Page')}
      />

      <Pressable
        disabled={!canGoForward}
        onPress={() => onPageChange(currentPage + 1)}
        testID="next-page-button"
        {...triggerProps}
      >
        <ChevronRight {...iconProps} />
      </Pressable>
      <Pressable
        disabled={!canGoForward}
        onPress={() => onPageChange(lastPage)}
        testID="last-page-button"
        {...triggerProps}
      >
        <ChevronLast {...iconProps} />
      </Pressable>
    </Row>
  );
};

const triggerProps: PressableProps = {
  withFeedback: true,
  withScale: true,
  hitSlop: 20,
  cursor: 'pointer',
  disabledStyle: {
    cursor: 'default'
  }
};

const iconProps: IconProps = {
  size: '$1.5',
  color: '$bodyFont'
};

Paginator.displayName = 'Paginator';
